import { useMap } from '@vis.gl/react-google-maps'
import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useQuery } from '../models'

export const BOUNDS_THRESHOLD = 0.01

export function boundsHaveMoved(mapBounds, storeBounds) {
  if (!mapBounds || !storeBounds) return false

  const sw = mapBounds.getSouthWest()
  const ne = mapBounds.getNorthEast()

  return (
    Math.abs(sw.lat() - storeBounds.south) > BOUNDS_THRESHOLD ||
    Math.abs(sw.lng() - storeBounds.west) > BOUNDS_THRESHOLD ||
    Math.abs(ne.lat() - storeBounds.north) > BOUNDS_THRESHOLD ||
    Math.abs(ne.lng() - storeBounds.east) > BOUNDS_THRESHOLD
  )
}

const SearchThisAreaButton = observer(() => {
  const { store } = useQuery()
  const { t } = useTranslation()
  const map = useMap()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const [visible, setVisible] = useState(false)
  const suppressRef = useRef(false)

  const searchStarted = store.searchStore.searchStarted
  const hasLocations = store.locationStore.hasLocations
  const canObserveBounds = searchStarted || hasLocations
  const loading = store.loading
  const storeBounds = store.boundingBox

  // Suppress the button briefly after a search completes,
  // because fitBounds triggers idle events as the map animates.
  useEffect(() => {
    suppressRef.current = true
    setVisible(false)
    const timer = setTimeout(() => {
      suppressRef.current = false
    }, 1000)
    return () => clearTimeout(timer)
  }, [storeBounds])

  useEffect(() => {
    if (!map || !canObserveBounds) return

    const listener = map.addListener('idle', () => {
      if (suppressRef.current) return

      const mapBounds = map.getBounds()
      if (boundsHaveMoved(mapBounds, storeBounds)) {
        setVisible(true)
      }
    })

    return () => listener.remove()
  }, [map, canObserveBounds, storeBounds])

  const handleClick = useCallback(() => {
    if (!map) return

    const mapBounds = map.getBounds()
    if (!mapBounds) return

    const sw = mapBounds.getSouthWest()
    const ne = mapBounds.getNorthEast()

    const bounds = {
      south: sw.lat(),
      west: sw.lng(),
      north: ne.lat(),
      east: ne.lng(),
    }

    setVisible(false)
    store.searchStore.searchByBounds(bounds)
  }, [map, store.searchStore])

  if (!visible || loading) return null

  return (
    <Box
      sx={{
        position: 'absolute',
        ...(mobile ? { bottom: 24, top: 'auto' } : { top: 80 }),
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1100,
      }}
    >
      <Button
        aria-label="Search This Area"
        variant="contained"
        color="primary"
        size="medium"
        startIcon={<SearchIcon />}
        onClick={handleClick}
        sx={{
          borderRadius: 5,
          textTransform: 'none',
          boxShadow: 3,
          px: 3,
          py: 1,
          fontSize: '0.95rem',
          bgcolor: 'background.paper',
          color: 'text.primary',
          '&:hover': {
            bgcolor: 'grey.100',
            boxShadow: 4,
          },
        }}
      >
        {t('Search this area')}
      </Button>
    </Box>
  )
})

SearchThisAreaButton.displayName = 'SearchThisAreaButton'

export default SearchThisAreaButton
